import { useCallback, useState } from 'react'
import toast from 'react-hot-toast'
import { trasladosService } from '../../api/trasladosService'
import { useAuth } from '../../contexts/AuthContext'
import type {
  DevolucionOrderDetail,
  DevolucionUnitLine,
  DevolucionUnitSelections
} from '../../types/devoluciones'
import type { CreateTrasladoPayload, TrasladoLinea } from '../../types/traslados'
import {
  buildOrderLineasForTraslado,
  getFlexyWarehouseId,
  groupUnitsForTraslado,
  ID_ALMACEN_NODO_EXTERNO
} from '../../utils/devoluciones-utils'
import { extractAxiosApiErrorMessage } from '../../utils/apiErrorMessage'

const ID_ALMACEN_DEVOLUCIONES = getFlexyWarehouseId('devoluciones')
const ID_ALMACEN_FULFILLMENT = getFlexyWarehouseId('fulfillment')
const ID_ALMACEN_SALDOS = getFlexyWarehouseId('saldos')

function buildTrasladoPayload(
  lineas: TrasladoLinea[],
  idalmacenOrigen: number,
  idalmacenDestino: number,
  usuarioCreacion: string,
  glosa: string,
  idPedido?: number | string
): CreateTrasladoPayload {
  return {
    idalmacen_origen: idalmacenOrigen,
    idalmacen_destino: idalmacenDestino,
    glosa,
    usuario_creacion: usuarioCreacion,
    lineas,
    ...(idPedido != null && String(idPedido).trim() !== ''
      ? { id_pedido: idPedido }
      : {})
  }
}

export function useDevolucionTraslado() {
  const { user } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const getUsuarioCreacion = useCallback(() => {
    const usuarioCreacion = user?.email?.trim() ?? ''
    if (!usuarioCreacion) {
      toast.error('No se pudo obtener el correo de la sesión')
      return null
    }
    return usuarioCreacion
  }, [user?.email])

  /** Pedidos en devolución: Nodo externo (0) → Devoluciones */
  const trasladarDesdeNodoExterno = useCallback(async (order: DevolucionOrderDetail) => {
    const usuarioCreacion = getUsuarioCreacion()
    if (!usuarioCreacion) return false

    const lineas = buildOrderLineasForTraslado(order.lineItems || [])
    if (!lineas.length) {
      toast.error('El pedido no tiene productos para transferir')
      return false
    }

    const payload = buildTrasladoPayload(
      lineas,
      ID_ALMACEN_NODO_EXTERNO,
      ID_ALMACEN_DEVOLUCIONES,
      usuarioCreacion,
      `Ingreso devolución pedido #${order.id} desde Nodo externo`,
      order.id
    )

    setIsSubmitting(true)
    try {
      await trasladosService.createTraslado(payload)
      return true
    } catch (error) {
      toast.error(extractAxiosApiErrorMessage(error, 'Error al crear la transferencia'))
      return false
    } finally {
      setIsSubmitting(false)
    }
  }, [getUsuarioCreacion])

  /** Devoluciones para transferir: Nodo externo (0) → Fulfillment / Saldos */
  const trasladar = useCallback(async (
    order: DevolucionOrderDetail,
    units: DevolucionUnitLine[],
    selections: DevolucionUnitSelections
  ) => {
    const usuarioCreacion = getUsuarioCreacion()
    if (!usuarioCreacion) return false

    const lineasFulfillment = groupUnitsForTraslado(units, selections, 'buen_estado')
    const lineasSaldos = groupUnitsForTraslado(units, selections, 'mal_estado')

    const payloads = [
      lineasFulfillment.length > 0
        ? buildTrasladoPayload(
          lineasFulfillment,
          ID_ALMACEN_NODO_EXTERNO,
          ID_ALMACEN_FULFILLMENT,
          usuarioCreacion,
          `Devolución pedido #${order.id} desde Nodo externo hacia Fulfillment`,
          order.id
        )
        : null,
      lineasSaldos.length > 0
        ? buildTrasladoPayload(
          lineasSaldos,
          ID_ALMACEN_NODO_EXTERNO,
          ID_ALMACEN_SALDOS,
          usuarioCreacion,
          `Devolución pedido #${order.id} desde Nodo externo hacia Saldos`,
          order.id
        )
        : null
    ].filter((payload): payload is CreateTrasladoPayload => Boolean(payload))

    if (!payloads.length) {
      toast.error('Selecciona al menos una unidad para transferir')
      return false
    }

    setIsSubmitting(true)
    try {
      const results = await Promise.allSettled(
        payloads.map((payload) => trasladosService.createTraslado(payload))
      )
      const rejected = results.filter((result) => result.status === 'rejected')

      if (rejected.length === 0) {
        toast.success(payloads.length > 1
          ? 'Transferencias creadas correctamente'
          : 'Transferencia creada correctamente')
        return true
      }

      if (rejected.length < results.length) {
        toast.error('Se creó una transferencia, pero otra falló. Revisa el listado de transferencias Flexy.')
        return false
      }

      const firstError = rejected[0]
      const reason = firstError.status === 'rejected' ? firstError.reason : null
      toast.error(extractAxiosApiErrorMessage(reason, 'Error al crear la transferencia'))
      return false
    } finally {
      setIsSubmitting(false)
    }
  }, [getUsuarioCreacion])

  return {
    isSubmitting,
    trasladar,
    trasladarDesdeNodoExterno
  }
}
